import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Loader2, Eye, EyeOff } from "lucide-react";
import logoAsset from "@/assets/logo-mi-almunajah.png.asset.json";
import { toast } from "sonner";
import { checkEmailExists } from "@/lib/auth.functions";
import { PasskeyLoginButton, rememberPasskeyEmail } from "@/components/passkey-login-button";

export const Route = createFileRoute("/auth")({
  head: () => ({ meta: [{ title: "Masuk — MI Al-Munajah Admin" }] }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) navigate({ to: "/dashboard", replace: true });
    });
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { exists } = await checkEmailExists({ data: { email } });
    if (!exists) {
      setLoading(false);
      return toast.error("Akun tidak terdaftar. Hubungi admin.");
    }

    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) return toast.error("Sandi Yang Anda Masukan Salah");
    rememberPasskeyEmail(email);
    if (typeof window !== "undefined" && window.innerWidth >= 768) {
      toast.success("Selamat datang kembali!");
    }
    navigate({ to: "/dashboard", replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-white p-1.5 flex items-center justify-center shadow-md">
            <img src={logoAsset.url} alt="MI Plus Almunajah" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-bold text-lg leading-tight">MI Plus Almunajah</div>
            <div className="text-xs opacity-80">Admin Panel</div>
          </div>
        </div>
        <div className="space-y-3">
          <h1 className="text-4xl font-bold leading-tight">Kelola sekolah<br/>dengan mudah.</h1>
          <p className="text-primary-foreground/80 max-w-md">
            Dashboard terpadu untuk data siswa, guru, pengumuman, jadwal, absensi, dan galeri sekolah secara realtime.
          </p>
        </div>
        <div className="text-xs opacity-70">© {new Date().getFullYear()} MI Al-Munajah</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <Card className="w-full max-w-md p-8 rounded-3xl shadow-[var(--shadow-elevation-2)]">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-white p-1 flex items-center justify-center shadow-sm border">
              <img src={logoAsset.url} alt="MI Plus Almunajah" className="w-full h-full object-contain" />
            </div>
            <div className="font-bold">MI Plus Almunajah</div>
          </div>
          <h2 className="text-2xl font-bold mb-1">Masuk ke Admin</h2>
          <p className="text-sm text-muted-foreground mb-6">Gunakan akun admin Anda.</p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
              </div>
              <div className="relative">
                <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" className="pr-10" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" tabIndex={-1}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button type="submit" className="flex-1 rounded-xl h-11" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Masuk
              </Button>
              <PasskeyLoginButton email={email} password={password} />
            </div>

          </form>

          <p className="text-xs text-muted-foreground text-center mt-6">
            Belum punya akun? Hubungi admin untuk mendapatkan undangan.
          </p>

        </Card>
      </div>
    </div>
  );
}
